<template>
  <div class="bg-[#F8FAFC] min-h-screen">
    <!-- HERO SECTION -->
    <section class="relative overflow-hidden bg-white py-16 lg:py-24">
      <div class="absolute top-0 right-0 w-96 h-96 bg-blue-100 rounded-full opacity-30 blur-3xl -translate-y-1/2 translate-x-1/2"></div>
      <div class="absolute bottom-0 left-0 w-72 h-72 bg-purple-100 rounded-full opacity-30 blur-3xl translate-y-1/2 -translate-x-1/2"></div>
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div class="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p class="text-[#2563EB] font-semibold text-sm tracking-wide uppercase mb-2">Indian IPO tracker</p>
            <h1 class="text-4xl lg:text-5xl font-bold text-[#1E293B] mb-4">Track every IPO from bidding to listing</h1>
            <p class="text-[#64748B] text-lg mb-8">Track Indian IPOs, GMP, subscription, allotment and listing dates in one place. Figures come from exchange issue data and the other sources listed on each page, with the time of the last update.</p>
            <dl class="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              <div><dd class="text-xl font-bold text-[#1E293B]">{{ count(year.count, state) }}</dd><dt class="text-xs text-[#64748B]">IPOs opened in {{ year.year || thisYear }}</dt></div>
              <div><dd class="text-xl font-bold text-[#1E293B]">{{ count(summary.open?.length, state) }}</dd><dt class="text-xs text-[#64748B]">Open for bidding now</dt></div>
              <div><dd class="text-xl font-bold text-[#1E293B]">{{ count(year.listed, state) }}</dd><dt class="text-xs text-[#64748B]">Listed in {{ year.year || thisYear }}</dt></div>
              <div><dd class="text-xl font-bold text-[#1E293B]">{{ count(summary.recently_listed?.length, state) }}</dd><dt class="text-xs text-[#64748B]">Listed in last 14 days (shown)</dt></div>
            </dl>
            <div class="flex flex-wrap gap-4">
              <router-link to="/ipos" class="bg-[#2563EB] text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition">Explore Current IPOs</router-link>
              <router-link to="/learn" class="border-2 border-[#2563EB] text-[#2563EB] px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition">How IPOs Work</router-link>
            </div>
          </div>
          <div class="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
            <div v-if="!state.loaded" class="animate-pulse space-y-4">
              <div class="h-6 bg-gray-200 rounded w-1/2"></div>
              <div class="h-24 bg-gray-200 rounded"></div>
            </div>
            <div v-else-if="state.error" class="text-sm text-[#64748B]">Market data is updating. Please refresh in a moment.</div>
            <div v-else>
              <p class="text-sm text-[#64748B] mb-3">Today at a glance · {{ summary.as_of_label }}</p>
              <div class="grid grid-cols-2 gap-4">
                <div class="bg-blue-50 rounded-lg p-3 text-center">
                  <p class="text-2xl font-bold text-[#2563EB]">{{ count(summary.open?.length, state) }}</p>
                  <p class="text-xs text-[#64748B]">Open for bidding</p>
                </div>
                <div class="bg-red-50 rounded-lg p-3 text-center">
                  <p class="text-2xl font-bold text-red-600">{{ count(summary.closing_soon?.length, state) }}</p>
                  <p class="text-xs text-[#64748B]">Closing within 2 days</p>
                </div>
                <div class="bg-purple-50 rounded-lg p-3 text-center">
                  <p class="text-2xl font-bold text-[#7C3AED]">{{ count(summary.awaiting_listing?.length, state) }}</p>
                  <p class="text-xs text-[#64748B]">Closed, awaiting listing</p>
                </div>
                <div class="bg-green-50 rounded-lg p-3 text-center">
                  <p class="text-2xl font-bold text-[#16A34A]">{{ count(summary.upcoming?.length, state) }}</p>
                  <p class="text-xs text-[#64748B]">Upcoming with dates</p>
                </div>
              </div>
              <p v-if="topSubscribed" class="text-xs text-[#64748B] mt-4">
                Highest overall subscription among open issues:
                <router-link :to="topSubscribed.url" class="font-semibold text-[#1E293B] hover:text-[#2563EB]">{{ topSubscribed.short_name }}</router-link>
                at {{ times(topSubscribed.subscription.total) }} ({{ topSubscribed.subscription.as_of }}).
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- MARKET UPDATE TICKER -->
    <section v-if="tickerItems.length" class="bg-gray-100 py-3 overflow-hidden">
      <div class="flex items-center animate-marquee whitespace-nowrap">
        <span v-for="(item, i) in tickerItems" :key="i" class="inline-flex items-center text-sm text-[#1E293B] mx-4">
          <span class="w-2 h-2 bg-[#2563EB] rounded-full mr-2"></span>{{ item }}
        </span>
        <router-link to="/subscriptions" class="inline-block py-2 text-[#2563EB] text-sm font-semibold ml-4 shrink-0">All subscription figures →</router-link>
      </div>
    </section>

    <!-- OPEN / UPCOMING IPO CARDS -->
    <section class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-2xl font-bold text-[#1E293B]">{{ cards.title }}</h2>
        <router-link to="/ipos" class="inline-block py-2 text-[#2563EB] font-semibold text-sm hover:underline">View All →</router-link>
      </div>
      <div v-if="!state.loaded" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div v-for="i in 4" :key="i" class="bg-white rounded-2xl border border-gray-100 p-5 animate-pulse">
          <div class="h-10 w-10 bg-gray-200 rounded-full mb-3"></div>
          <div class="h-4 bg-gray-200 rounded mb-2 w-3/4"></div>
          <div class="h-8 bg-gray-200 rounded"></div>
        </div>
      </div>
      <p v-else-if="!cards.items.length" class="text-[#64748B]">No IPO is open for bidding and no upcoming dates are confirmed in our records right now.</p>
      <div v-else class="flex gap-6 overflow-x-auto pb-4 snap-x">
        <div v-for="ipo in cards.items" :key="ipo.slug" class="min-w-[280px] max-w-[320px] snap-start bg-white rounded-2xl border border-gray-100 p-5 flex flex-col">
          <div class="flex items-center gap-3 mb-3">
            <CompanyLogo :name="ipo.name" :logo-url="ipo.logo_url" size="md" />
            <div class="min-w-0">
              <p class="font-semibold text-[#1E293B] text-sm line-clamp-2">{{ ipo.name }}</p>
              <p class="text-xs text-[#64748B]">{{ ipo.exchange || '—' }}</p>
            </div>
          </div>
          <div class="space-y-1 text-sm mb-3">
            <p class="text-[#64748B]">Price: <span class="text-[#1E293B]">{{ ipo.price_band ? ipo.price_band.replace(' per share', '') : '—' }}</span></p>
            <p class="text-[#64748B]">Subscription: <span class="text-[#1E293B]">{{ ipo.subscription ? times(ipo.subscription.total) + ' (' + ipo.subscription.as_of + ')' : 'Not yet reported' }}</span></p>
            <p class="text-[#64748B]">GMP <span class="text-[10px]">(unofficial)</span>: <span class="text-[#1E293B]">{{ ipo.gmp ? rupees(ipo.gmp.value) + ' (' + ipo.gmp.as_of + ')' : '—' }}</span></p>
            <p class="text-[#64748B]">{{ closesLabel(ipo) }}</p>
          </div>
          <router-link :to="ipo.url" class="mt-auto text-center bg-[#2563EB] text-white text-sm py-2 rounded-lg hover:bg-blue-700 transition">View Details</router-link>
        </div>
      </div>
    </section>

    <!-- TODAY / ACTIVITY / WEEK — generated from our own database -->
    <section v-if="state.loaded && !state.error" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div class="bg-white rounded-2xl border border-gray-100 p-6 lg:col-span-2">
        <h2 class="text-xl font-bold text-[#1E293B] mb-3">Today's IPO market</h2>
        <div class="space-y-3 text-[#334155] leading-relaxed text-sm">
          <p v-for="(p, i) in summary.paragraphs?.today" :key="'t' + i">{{ p }}</p>
        </div>
        <h2 class="text-xl font-bold text-[#1E293B] mt-8 mb-3">Latest IPO activity</h2>
        <div class="space-y-3 text-[#334155] leading-relaxed text-sm">
          <p v-for="(p, i) in summary.paragraphs?.activity" :key="'a' + i">{{ p }}</p>
        </div>
        <DataFreshness :value="summary.freshness" />
      </div>
      <div class="space-y-6">
        <div class="bg-white rounded-2xl border border-gray-100 p-6">
          <h2 class="font-bold text-lg text-[#1E293B] mb-1">This week in IPOs</h2>
          <p class="text-xs text-[#64748B] mb-3">{{ summary.week_label }}</p>
          <div class="space-y-2 text-sm text-[#334155]">
            <p v-for="(p, i) in summary.paragraphs?.week" :key="'w' + i">{{ p }}</p>
          </div>
          <router-link to="/ipos/calendar" class="inline-block mt-3 text-sm font-semibold text-[#2563EB] hover:underline">Full IPO calendar →</router-link>
        </div>
        <div class="bg-white rounded-2xl border border-gray-100 p-6">
          <h2 class="font-bold text-lg text-[#1E293B] mb-4">Most subscribed open issues</h2>
          <ul v-if="summary.top_subscribed?.length" class="space-y-3">
            <li v-for="(ipo, idx) in summary.top_subscribed" :key="ipo.slug" class="flex items-center gap-3">
              <span class="text-sm font-bold text-[#64748B] w-4 shrink-0">{{ idx + 1 }}</span>
              <router-link :to="ipo.url" class="flex-1 min-w-0 text-sm font-semibold text-[#1E293B] truncate hover:text-[#2563EB]">{{ ipo.short_name }}</router-link>
              <span class="text-sm font-bold text-[#2563EB]">{{ times(ipo.subscription.total) }}</span>
            </li>
          </ul>
          <p v-else class="text-sm text-[#64748B]">No subscription figures are recorded for open issues yet.</p>
          <p class="text-xs text-[#64748B] mt-3">Overall multiple, as of the date shown on each IPO page.</p>
        </div>
      </div>
    </section>

    <!-- TOOLS -->
    <section class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
      <h2 class="text-2xl font-bold text-[#1E293B] mb-6">Tools</h2>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <router-link v-for="tool in tools" :key="tool.title" :to="tool.link" class="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md transition group">
          <div class="text-3xl mb-3">{{ tool.icon }}</div>
          <h3 class="font-semibold text-[#1E293B] text-sm mb-1">{{ tool.title }}</h3>
          <p class="text-xs text-[#64748B] mb-3">{{ tool.desc }}</p>
          <span class="text-xs font-semibold" :class="tool.color">{{ tool.cta }} →</span>
        </router-link>
      </div>
    </section>

    <!-- GUIDES -->
    <section v-if="guides.length" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-2xl font-bold text-[#1E293B]">Guides to how IPOs work</h2>
        <router-link to="/learn" class="inline-block py-2 text-[#2563EB] font-semibold text-sm hover:underline">All guides →</router-link>
      </div>
      <GuideCards :guides="guides" />
    </section>

    <!-- LATEST NEWS -->
    <section v-if="news.length" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-2xl font-bold text-[#1E293B]">Latest news and roundups</h2>
        <router-link to="/news" class="inline-block py-2 text-[#2563EB] font-semibold text-sm hover:underline">View All News →</router-link>
      </div>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <router-link v-for="article in news" :key="article.slug" :to="article.url" class="bg-white rounded-2xl border border-gray-100 p-4 hover:shadow-md transition">
          <div class="flex items-center gap-2 mb-2">
            <span class="text-xs bg-blue-50 text-[#2563EB] px-2 py-0.5 rounded-full">{{ article.category || 'News' }}</span>
            <span class="text-xs text-[#64748B]">{{ article.published_label }}</span>
          </div>
          <h3 class="font-semibold text-sm text-[#1E293B] mb-2 line-clamp-2">{{ article.title }}</h3>
          <p class="text-xs text-[#64748B]">{{ article.byline }}</p>
        </router-link>
      </div>
    </section>

    <section class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
      <p class="text-xs text-[#64748B] max-w-4xl">Niva Market Watch publishes factual IPO data and educational content. It does not recommend applying for, buying or selling any security. Read our <router-link to="/methodology" class="underline">data methodology</router-link> and <router-link to="/disclaimer" class="underline">disclaimer</router-link>.</p>
    </section>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import axios from 'axios'
import CompanyLogo from '../components/CompanyLogo.vue'
import DataFreshness from '../components/DataFreshness.vue'
import GuideCards from '../components/GuideCards.vue'
import { count, times, rupees } from '../format'

const thisYear = new Date().getFullYear()
const state = reactive({ loaded: false, error: false })
const summary = ref({})
const news = ref([])
const guides = ref([])

const year = computed(() => summary.value.year || {})
const topSubscribed = computed(() => summary.value.top_subscribed?.[0] || null)

const cards = computed(() => {
  const open = summary.value.open || []
  if (open.length) return { title: 'Open for bidding', items: open }
  return { title: 'Upcoming IPOs', items: (summary.value.upcoming || []).slice(0, 6) }
})

const tickerItems = computed(() =>
  (summary.value.open || [])
    .filter(i => i.subscription)
    .map(i => `${i.short_name}: subscribed ${times(i.subscription.total)} (${i.subscription.as_of})`)
)

const tools = [
  { icon: '📋', title: 'Subscription Status', desc: 'Overall and category-wise subscription', cta: 'Check', color: 'text-[#7C3AED]', link: '/subscriptions' },
  { icon: '📈', title: 'GMP Tracker', desc: 'Unofficial grey market quotes, dated', cta: 'Track', color: 'text-[#16A34A]', link: '/gmp' },
  { icon: '🎯', title: 'Allotment Dates', desc: 'Allotment, refund and listing dates', cta: 'Check', color: 'text-orange-500', link: '/allotments' },
  { icon: '🗓️', title: 'IPO Calendar', desc: 'Every key date, day by day', cta: 'Open', color: 'text-[#2563EB]', link: '/ipos/calendar' },
  { icon: '⚖️', title: 'Compare IPOs', desc: 'Two issues side by side', cta: 'Compare', color: 'text-[#2563EB]', link: '/compare' },
  { icon: '📚', title: 'IPO Guides', desc: 'How IPOs work, step by step', cta: 'Read', color: 'text-pink-500', link: '/learn' },
]

function closesLabel(ipo) {
  if (ipo.status === 'upcoming') return ipo.open_label ? `Opens ${ipo.open_label}` : 'Opening date not announced'
  if (!ipo.close_date) return 'Closing date not available'
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const diff = Math.round((new Date(ipo.close_date + 'T00:00:00') - today) / 86400000)
  if (diff < 0) return `Closed ${ipo.close_label}`
  if (diff === 0) return 'Closes today'
  return `Closes in ${diff} day${diff === 1 ? '' : 's'} (${ipo.close_label})`
}

onMounted(async () => {
  try {
    const { data } = await axios.get('/api/home')
    summary.value = data.summary || {}
    news.value = data.news || []
    guides.value = data.guides || []
  } catch (e) {
    state.error = true
  } finally {
    state.loaded = true
  }
})
</script>

<style scoped>
@keyframes marquee {
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}
.animate-marquee {
  animation: marquee 30s linear infinite;
}
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
